import React, { useState, useEffect } from 'react';
import { invoiceAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { FileText, Save } from 'lucide-react';
import { toast } from 'sonner';

export default function InvoiceSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    institution_name: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    phone: '',
    email: '',
    website: '',
    gstin: '',
    pan: '',
    license_number: '',
    registration_number: '',
    header_text: '',
    footer_text: 'Thank you for choosing our services',
    terms_and_conditions: 'Payment is due upon receipt. Please retain this invoice for your records.',
    show_gstin: true,
    show_pan: false,
    show_license: true,
    invoice_prefix: 'INV'
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await invoiceAPI.getSettings();
      if (response.data) {
        setSettings(response.data);
      }
    } catch (error) {
      toast.error('Failed to load invoice settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await invoiceAPI.updateSettings(settings);
      toast.success('Invoice settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-slate-500">Loading invoice settings...</div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-[Manrope]">Invoice Settings</h1>
          <p className="text-slate-500 mt-1">Customize your invoice layout and details</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-red-500 hover:bg-red-600" data-testid="save-invoice-settings-btn">
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>

      {/* Institution Details */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold font-[Manrope]">
            <FileText className="w-5 h-5 inline mr-2 text-red-500" />
            Institution Details
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Institution Name *</Label>
            <Input
              value={settings.institution_name}
              onChange={(e) => setSettings({...settings, institution_name: e.target.value})}
              placeholder="Hospital/Clinic Name"
            />
          </div>
          <div>
            <Label>Email *</Label>
            <Input
              type="email"
              value={settings.email}
              onChange={(e) => setSettings({...settings, email: e.target.value})}
              placeholder="contact@hospital.com"
            />
          </div>
          <div>
            <Label>Phone *</Label>
            <Input
              value={settings.phone}
              onChange={(e) => setSettings({...settings, phone: e.target.value})}
              placeholder="+91 XXXXXXXXXX"
            />
          </div>
          <div>
            <Label>Website</Label>
            <Input
              value={settings.website}
              onChange={(e) => setSettings({...settings, website: e.target.value})}
              placeholder="https://www.hospital.com"
            />
          </div>
          <div className="md:col-span-2">
            <Label>Address *</Label>
            <Textarea
              value={settings.address}
              onChange={(e) => setSettings({...settings, address: e.target.value})}
              placeholder="Street address"
              rows={2}
            />
          </div>
          <div>
            <Label>City *</Label>
            <Input
              value={settings.city}
              onChange={(e) => setSettings({...settings, city: e.target.value})}
              placeholder="City"
            />
          </div>
          <div>
            <Label>State *</Label>
            <Input
              value={settings.state}
              onChange={(e) => setSettings({...settings, state: e.target.value})}
              placeholder="State"
            />
          </div>
          <div>
            <Label>Pincode *</Label>
            <Input
              value={settings.pincode}
              onChange={(e) => setSettings({...settings, pincode: e.target.value})}
              placeholder="XXXXXX"
            />
          </div>
        </CardContent>
      </Card>

      {/* Registration Numbers */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold font-[Manrope]">Registration Numbers</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>GSTIN (GST Number)</Label>
            <Input
              value={settings.gstin}
              onChange={(e) => setSettings({...settings, gstin: e.target.value})}
              placeholder="22AAAAA0000A1Z5"
            />
            <div className="flex items-center space-x-2 mt-2">
              <Checkbox
                id="show_gstin"
                checked={settings.show_gstin}
                onCheckedChange={(checked) => setSettings({...settings, show_gstin: checked})}
              />
              <Label htmlFor="show_gstin" className="text-sm cursor-pointer">Show on invoice</Label>
            </div>
          </div>
          <div>
            <Label>PAN Number</Label>
            <Input
              value={settings.pan}
              onChange={(e) => setSettings({...settings, pan: e.target.value})}
              placeholder="AAAAA0000A"
            />
            <div className="flex items-center space-x-2 mt-2">
              <Checkbox
                id="show_pan"
                checked={settings.show_pan}
                onCheckedChange={(checked) => setSettings({...settings, show_pan: checked})}
              />
              <Label htmlFor="show_pan" className="text-sm cursor-pointer">Show on invoice</Label>
            </div>
          </div>
          <div>
            <Label>Medical License Number</Label>
            <Input
              value={settings.license_number}
              onChange={(e) => setSettings({...settings, license_number: e.target.value})}
              placeholder="License Number"
            />
            <div className="flex items-center space-x-2 mt-2">
              <Checkbox
                id="show_license"
                checked={settings.show_license}
                onCheckedChange={(checked) => setSettings({...settings, show_license: checked})}
              />
              <Label htmlFor="show_license" className="text-sm cursor-pointer">Show on invoice</Label>
            </div>
          </div>
          <div>
            <Label>Other Registration Number</Label>
            <Input
              value={settings.registration_number}
              onChange={(e) => setSettings({...settings, registration_number: e.target.value})}
              placeholder="Other registration/permit number"
            />
          </div>
        </CardContent>
      </Card>

      {/* Customization */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold font-[Manrope]">Invoice Customization</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Invoice Number Prefix</Label>
            <Input
              value={settings.invoice_prefix}
              onChange={(e) => setSettings({...settings, invoice_prefix: e.target.value})}
              placeholder="INV"
              maxLength={5}
            />
            <p className="text-xs text-slate-500 mt-1">Format: {settings.invoice_prefix}-2024-0001</p>
          </div>
          <div>
            <Label>Header Text (Optional)</Label>
            <Textarea
              value={settings.header_text}
              onChange={(e) => setSettings({...settings, header_text: e.target.value})}
              placeholder="Custom header message to appear at top of invoice"
              rows={2}
            />
          </div>
          <div>
            <Label>Footer Text</Label>
            <Textarea
              value={settings.footer_text}
              onChange={(e) => setSettings({...settings, footer_text: e.target.value})}
              placeholder="Thank you message"
              rows={2}
            />
          </div>
          <div>
            <Label>Terms and Conditions</Label>
            <Textarea
              value={settings.terms_and_conditions}
              onChange={(e) => setSettings({...settings, terms_and_conditions: e.target.value})}
              placeholder="Payment terms, policies, etc."
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      {/* Preview Info */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> These settings will be used when generating invoices for patients. You can further customize individual invoices at the time of generation.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
